import { AuthInfo, StateModel, UserInfo } from '../models/state.model';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchRefreshToken, getToken, getUserClaims } from '../../api/resource';
import { clearTokens, saveTokens } from '../../utils/authStorage';
import { AuthRefreshToken } from '../../models/auth/AuthToken';
import { OAuth2Claims } from '../../models/auth/OAuth2Claims';

const initialState: AuthInfo = {
  authorized: false,
  userInfo: null,
  isLoading: false,
  isRefreshed: false,
};

export const fetchToken = createAsyncThunk(
  'auth/token',
  async (arg: { code: string; redirect: () => void }) => {
    const response = await getToken(arg.code);
    return response.data;
  },
  {
    condition: (_, { getState }) => {
      const { authInfo } = getState() as StateModel;
      return !authInfo.isLoading;
    },
  },
);

export const refreshAuthToken = createAsyncThunk(
  'auth/refreshToken',
  async (refreshToken: string) => {
    const response = await fetchRefreshToken(refreshToken);
    return response.data;
  },
  {
    condition: (_, { getState }) => {
      const { authInfo } = getState() as StateModel;
      return !authInfo.isRefreshed;
    },
  },
);

const mapClaimsToUserInfo = (claims: OAuth2Claims): UserInfo => ({
  username: claims.sub,
  fullName: claims.full_name,
  email: claims.email,
  isAnonymous: claims.is_anonymous,
  avatarUrl: claims.avatar_url,
});

export const fetchUserInfo = createAsyncThunk(
  'auth/userInfo',
  async (token: string) => {
    const response = await getUserClaims(token);
    return response.status === 200 ? response.data : null;
  },
  {
    condition: (args, { getState }) => {
      const { authInfo } = getState() as StateModel;
      return args !== null && !authInfo.isLoading;
    },
  },
);

const saveAuthData = (state: AuthInfo, action: PayloadAction<AuthRefreshToken>) => {
  const { access_token, refresh_token, id_token, scope, token_type, expires_in } = action.payload;

  state.authorized = true;
  saveTokens({
    access_token,
    refresh_token,
    id_token,
    scope,
    token_type,
    expires_in,
  });
};

const authSlice = createSlice({
  name: 'authInfo',
  initialState,
  reducers: {
    userLoggedIn(state) {
      state.authorized = true;
    },
    userLoggedOut(state) {
      state.authorized = false;
      state.userInfo = null;
      state.isLoading = false;
      state.isRefreshed = false;
      clearTokens();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchToken.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchToken.fulfilled, (state, action) => {
      state.isLoading = false;
      action.meta.arg.redirect();
      saveAuthData(state, action);
    });
    builder.addCase(fetchToken.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(refreshAuthToken.pending, (state) => {
      state.isRefreshed = true;
    });
    builder.addCase(refreshAuthToken.fulfilled, (state, action) => {
      state.isRefreshed = false;
      saveAuthData(state, action);
    });
    builder.addCase(refreshAuthToken.rejected, (state) => {
      state.isRefreshed = false;
      clearTokens();
    });
    builder.addCase(fetchUserInfo.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchUserInfo.fulfilled, (state: AuthInfo, action) => {
      if (action.payload) {
        state.userInfo = mapClaimsToUserInfo(action.payload);
      }
      state.isLoading = false;
    });
    builder.addCase(fetchUserInfo.rejected, (state: AuthInfo) => {
      state.isLoading = false;
    });
  },
});

export const { userLoggedIn, userLoggedOut } = authSlice.actions;

const AuthInfoReducer = authSlice.reducer;

export default AuthInfoReducer;